import React, { useState, useEffect} from 'react';
import { Canvas } from '@react-three/fiber';
// import { Stats } from '@react-three/drei';
// import * as THREE from 'three';
import Scene from './components/Scene';
import SectionHero from './components/SectionHero';
import Sections from './components/Sections';
import Navbar from './components/Navbar';
import VideoModal from './components/VideoModal';
import projects from './data/projects';
import { useAtom, atom } from 'jotai';
import { activeBubbleAtom, isAnyBubbleHoveredAtom } from './components/ProjectBubbles';
import './components/BubbleTooltip.css';
import './components/VideoControls.css';
import SoundOnIcon from "/ui/sound_on.svg";
import SoundOffIcon from "/ui/sound_off.svg";
import PlayIcon from "/ui/play.svg";
import PauseIcon from "/ui/pause.svg";

// Create a global atom for video state
export const videoStateAtom = atom({
  isVisible: false,
  isPlaying: false,
  isLoaded: false,
  isMuted: true,
  videoPath: '',
  togglePlayPause: false,
  mobileActivated: false,
  duration: 0,        // Total duration of the video in seconds
  currentTime: 0,     // Current playback position in seconds
  restartVideo: false, // Flag to restart the video
  hasUserInteracted: false, // Track if user has manually controlled playback
  savedPlaybackPosition: 0 // Store playback position across component remounts
});

// Component to display the tooltip for the active bubble
const BubbleTooltip = ({ onBubbleClick }) => {
  const [activeBubble] = useAtom(activeBubbleAtom);
  const [visible, setVisible] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Update tooltip content and visibility when active bubble changes
  useEffect(() => {
    if (activeBubble) {
      // Get the current bubble index
      const bubbleIndex = activeBubble.index;
      
      // Only show tooltips when we're between the first and last bubble (inclusive)
      // This ensures tooltips only appear after the first bubble is centered
      // and disappear after the last bubble leaves the center
      if (bubbleIndex >= 0 && bubbleIndex < projects.length) {
        setTooltipContent(projects[bubbleIndex].title);
        setActiveIndex(bubbleIndex);
        setVisible(true);
      } else {
        setVisible(false);
      }
    } else {
      // When there's no active bubble, hide the tooltip
      setVisible(false);
    }
  }, [activeBubble]);
  
  // Handle click on the tooltip
  const handleClick = () => {
    if (activeBubble && onBubbleClick) {
      // Get the project data for the active bubble
      const project = projects[activeBubble.index];
      if (project) {
        // Generate the video path
        const videoPath = `/projects/${project.slug}.mp4`;
        // Call the onBubbleClick function with the video path and project
        onBubbleClick(videoPath, project);
      }
    }
  };
  
  if (!tooltipContent) return null;
  
  // Calculate the offset to center the active dot with the tooltip label
  const calculateOffset = () => {
    if (!activeBubble) return 0;
    
    // Get the index of the active bubble
    const index = activeBubble.index;
    
    // Calculate the offset based on the index
    // Each dot is 8px tall with 12px gap, so each dot position is at (index * (8 + 12))
    // We want to center the active dot, so we subtract half the list height
    const dotHeight = 8; // Height of each dot
    const dotGap = 12;   // Gap between dots
    const totalDots = projects.length;
    
    // Calculate the position of the active dot from the top of the list
    const activeDotPosition = index * (dotHeight + dotGap);
    
    // Calculate the total height of the list
    const listHeight = totalDots * dotHeight + (totalDots - 1) * dotGap;
    
    // Calculate the offset to center the active dot
    // We want the active dot to be at the center of the viewport
    // So we offset the list by: -(activeDotPosition - (listHeight / 2 - dotHeight / 2))
    return -(activeDotPosition - (listHeight / 2 - dotHeight / 2));
  };
  
  // Calculate the offset
  const offset = calculateOffset();
  
  return (
    <>
      {visible && (
        <ul 
          className='tooltip-list'
          style={{ 
            transform: `translateY(calc(-50% + ${offset}px))`
          }}
        >
          {projects.map((project, index) => (
            <li 
              key={index} 
              className={`nav-dot ${activeBubble && activeBubble.index === index ? 'active' : ''}`}
              onClick={() => {
                if (onBubbleClick) {
                  const videoPath = `/projects/${project.slug}.mp4`;
                  onBubbleClick(videoPath, project);
                }
              }}
            />
          ))}
        </ul>
      )}
      <div 
        onClick={handleClick}
        className={`bubble-tooltip ${visible ? 'visible' : 'hidden'}`}
      >
        <div className='bubble-label'>{tooltipContent}</div>
      </div>
    </>
  );
};

// Component to display video controls
const VideoControls = () => {
  const [videoState, setVideoState] = useAtom(videoStateAtom);
  
  // Handle toggle audio
  const handleToggleAudio = () => {
    setVideoState(prev => ({
      ...prev,
      toggleAudio: true
    }));
  };
  
  // Handle play/pause
  const handlePlayPause = () => {
    const isMobile = window.innerWidth <= 768;
    const newPlayingState = !videoState.isPlaying;
    
    console.log("Play/Pause clicked. Current state:", videoState.isPlaying, "New state:", newPlayingState);
    
    // If pausing, save the current playback position first
    if (videoState.isPlaying && !newPlayingState) {
      console.log("Saving current playback position:", videoState.currentTime);
      setVideoState(prev => ({
        ...prev,
        savedPlaybackPosition: prev.currentTime,
        togglePlayPause: true,
        isPlaying: false,
        mobileActivated: true,
        hasUserInteracted: true
      }));
      return;
    }
    
    // For mobile first-time activation, handle differently
    if (isMobile && !videoState.mobileActivated && newPlayingState) {
      console.log("First play on mobile, activating video");
      setVideoState(prev => ({
        ...prev,
        togglePlayPause: true,
        isPlaying: true,
        mobileActivated: true,
        hasUserInteracted: true
      }));
    } else {
      // Normal play/pause toggle
      setVideoState(prev => ({
        ...prev,
        togglePlayPause: true,
        isPlaying: newPlayingState,
        mobileActivated: true,
        hasUserInteracted: true
      }));
    }
  };
  
  return (
    <div className={`video-control-container`}>
      {/* Play/Pause button */}
      <button 
        className="video-control-play-button"
        onClick={handlePlayPause}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handlePlayPause();
          }
        }}
        aria-label={videoState.isPlaying ? "Pause video" : "Play video"}
        aria-pressed={videoState.isPlaying}
      >
        <img 
          src={videoState.isPlaying ? PauseIcon : PlayIcon} 
          alt={videoState.isPlaying ? "Pause" : "Play"} 
        />
      </button>
      
      {/* Audio toggle button */}
      <button 
        className="video-control-audio-button"
        onClick={handleToggleAudio}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggleAudio();
          }
        }}
        aria-label={videoState.isMuted ? "Unmute video" : "Mute video"}
        aria-pressed={!videoState.isMuted}
      >
        <img 
          src={videoState.isMuted ? SoundOnIcon : SoundOffIcon} 
          alt={videoState.isMuted ? "Turn sound on" : "Turn sound off"} 
        />
      </button>


      {/* Timeline with restart functionality */}
      <div 
        className="video-control-timeline"
        style={{
          // Set the progress as a CSS variable
          '--progress': `${(videoState.currentTime / videoState.duration) * 100}%`
        }}
        onClick={() => {
          // Set restart flag to true
          setVideoState(prev => ({
            ...prev,
            restartVideo: true,
            isPlaying: true // Ensure video plays after restart
          }));
        }}
      >
        {/* Display remaining time in seconds, wrapped in a span for z-index */}
        <span>{Math.max(0, Math.floor(videoState.duration - videoState.currentTime))}</span>
      </div>
    </div>
  );
};

function App() {
  const [scrollY, setScrollY] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const [activeVideo, setActiveVideo] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const [activeProjectIndex, setActiveProjectIndex] = useState(-1);
  const [portalProgress, setPortalProgress] = useState(0);
  const [showIntro, setShowIntro] = useState(true);
  const [showScene, setShowScene] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeBubble, setActiveBubble] = useAtom(activeBubbleAtom);
  const [isAnyBubbleHovered, setIsAnyBubbleHovered] = useAtom(isAnyBubbleHoveredAtom);
  const [sizes, setSizes] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      const newSection = Math.round(window.scrollY / sizes.height);
      if (newSection !== currentSection) {
        setCurrentSection(newSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentSection, sizes.height]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setSizes({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Navbar />
      <main id="main-content">
        <Canvas
          className="webgl"
          style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}
        camera={{
          fov: 35,
          near: 0.1,
          far: 1000,
          position: [0, 0, 10],
          up: [0, 1, 0]
        }}
        gl={{ 
          antialias: true,
          alpha: true,
          sortObjects: true, // Enable sorting of transparent objects
          logarithmicDepthBuffer: true, // Improve depth precision
          precision: "highp" // Use high precision for better rendering
        }}
        eventSource={document.getElementById('root')} eventPrefix="client"
      >
        {/* <Stats showPanel={0} className="stats" /> */}
        <Scene 
          scrollY={scrollY} 
          sizes={sizes}
          modalOpen={modalOpen}
          onVideoSelect={(videoPath, project) => {
            setActiveVideo(videoPath);
            setActiveProject(project);
            setActiveProjectIndex(projects.findIndex(p => p.id === project.id));
            setModalOpen(true);
          }} />
      </Canvas>

      {/* HTML Section Elements */}
      <SectionHero scrollY={scrollY} />
      <Sections />
      
      {/* Bubble Tooltip - rendered outside of Canvas as a regular DOM element */}
      {/* Only render when in the visible area (similar to showBubbles in Scene.jsx) and modal is not open */}
      {scrollY >= sizes.height * 0.9 && scrollY <= sizes.height * 4.4 && !modalOpen && (
        <BubbleTooltip 
          onBubbleClick={(videoPath, project) => {
            setActiveVideo(videoPath);
            setActiveProject(project);
            setActiveProjectIndex(projects.findIndex(p => p.id === project.id));
            setModalOpen(true);
          }}
        />
      )}
      
      {/* Video Controls - rendered outside of Canvas for better DOM integration */}
       {/* Only render when in the visible area (similar to showBubbles in Scene.jsx) and modal is not open */}
      {scrollY >= sizes.height * 4.5 && scrollY <= sizes.height * 5.9 && !modalOpen && (
        <VideoControls />
      )}
      
      {/* Video Modal - rendered outside of Canvas for better DOM integration */}
      {activeVideo && activeProject && (
        <VideoModal 
          videoPath={activeVideo}
          project={activeProject}
          onClose={() => {
            setActiveVideo(null);
            setActiveProject(null);
            setActiveProjectIndex(-1);
            setModalOpen(false);
            setActiveBubble(null);
            setIsAnyBubbleHovered(false);
          }}
          onNavigate={(direction) => {
            // Calculate the new index based on the direction
            let newIndex;
            if (direction === "next") {
              newIndex = (activeProjectIndex + 1) % projects.length;
            } else if (direction === "prev") {
              newIndex = (activeProjectIndex - 1 + projects.length) % projects.length;
            } else {
              // If direction is a number, use it as the index
              newIndex = typeof direction === 'number' ? direction : activeProjectIndex;
            }
            
            // Get the new project
            const newProject = projects[newIndex];
            
            // Update the state
            setActiveProject(newProject);
            setActiveProjectIndex(newIndex);
            setActiveVideo(`/projects/${newProject.slug}.mp4`);
          }}
        />
      )}
      </main>
    </>
  );
}

export default App;
